import React, { useState, useMemo, useCallback, useEffect } from 'react';
import axios from 'axios';
import { FileBarChart, Download, Eye, FileSpreadsheet, Loader2, Calendar, Building2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENT_YEAR = new Date().getFullYear();

const FTAFiling = () => {
  const { toast } = useToast();
  const token = localStorage.getItem('admin_token');

  const [periodType, setPeriodType] = useState('quarter');
  const [year, setYear] = useState(CURRENT_YEAR);
  const [periodValue, setPeriodValue] = useState(1);
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);

  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  // Load companies list
  useEffect(() => {
    axios.get(`${API_URL}/api/procurement/companies`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => setCompanies(r.data || [])).catch(() => {});
  }, [token]);

  const query = useMemo(() => {
    const q = new URLSearchParams();
    q.append('period_type', periodType);
    q.append('year', year);
    if (periodType !== 'annual') q.append('period_value', periodValue);
    if (companyId) q.append('company_id', companyId);
    return q.toString();
  }, [periodType, year, periodValue, companyId]);

  const generateSummary = useCallback(async () => {
    setLoading(true);
    setSummary(null);
    try {
      const r = await axios.get(`${API_URL}/api/finance/fta-filing/summary?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(r.data);
    } catch (err) {
      toast({ variant: 'destructive', title: 'Erro', description: err.response?.data?.detail || 'Falha ao carregar' });
    } finally {
      setLoading(false);
    }
  }, [query, token, toast]);

  const downloadPDF = () => window.open(`${API_URL}/api/finance/fta-filing/pdf/download?${query}&token=${token}`, '_blank');
  const previewPDF = () => window.open(`${API_URL}/api/finance/fta-filing/pdf/preview?${query}&token=${token}`, '_blank');
  const downloadCSV = () => window.open(`${API_URL}/api/finance/fta-filing/csv/download?${query}&token=${token}`, '_blank');

  const fmt = (n) => new Intl.NumberFormat('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(n) || 0);

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="fta-filing-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-[#2B2D42] flex items-center gap-2">
          <FileBarChart className="h-7 w-7 text-[#D97706]" />
          FTA Filing Preparation
        </h1>
        <p className="text-sm text-[#6C757D] mt-1">
          Corporate Tax aggregation report for UAE Federal Tax Authority submission (draft).
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Calendar className="h-4 w-4" /> Period Selection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-600">Period Type</label>
              <select
                value={periodType}
                onChange={(e) => setPeriodType(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-lg bg-white"
                data-testid="period-type"
              >
                <option value="quarter">Quarter</option>
                <option value="annual">Annual</option>
                <option value="month">Month</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-600">Year</label>
              <input
                type="number"
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="mt-1 w-full px-3 py-2 border rounded-lg"
                data-testid="year-input"
              />
            </div>
            {periodType !== 'annual' && (
              <div>
                <label className="text-xs font-medium text-gray-600">
                  {periodType === 'quarter' ? 'Quarter' : 'Month'}
                </label>
                <select
                  value={periodValue}
                  onChange={(e) => setPeriodValue(Number(e.target.value))}
                  className="mt-1 w-full px-3 py-2 border rounded-lg bg-white"
                  data-testid="period-value"
                >
                  {periodType === 'quarter'
                    ? [1, 2, 3, 4].map((q) => <option key={q} value={q}>Q{q}</option>)
                    : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                        <option key={m} value={m}>
                          {new Date(year, m - 1, 1).toLocaleString('en', { month: 'long' })}
                        </option>
                      ))}
                </select>
              </div>
            )}
            <div>
              <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                <Building2 className="h-3 w-3" /> Company
              </label>
              <select
                value={companyId}
                onChange={(e) => setCompanyId(e.target.value)}
                className="mt-1 w-full px-3 py-2 border rounded-lg bg-white"
                data-testid="company-filter"
              >
                <option value="">All Companies</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.legal_name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button
                onClick={generateSummary}
                disabled={loading}
                className="w-full bg-[#D97706] hover:bg-[#92400E]"
                data-testid="generate-summary-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileBarChart className="w-4 h-4 mr-2" />}
                Generate
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {summary && (
        <>
          {/* Export Actions */}
          <Card className="mb-6 border-amber-200 bg-amber-50/40">
            <CardContent className="pt-4 pb-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-base font-bold text-amber-900">{summary.period_label}</h3>
                  <p className="text-xs text-amber-700">
                    {summary.start_date} → {summary.end_date} · {summary.totals.invoice_count} invoices ·{' '}
                    {summary.companies.length} compan{summary.companies.length === 1 ? 'y' : 'ies'}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={previewPDF} data-testid="preview-pdf">
                    <Eye className="w-4 h-4 mr-2" /> Preview PDF
                  </Button>
                  <Button className="bg-[#D97706] hover:bg-[#92400E]" onClick={downloadPDF} data-testid="download-pdf">
                    <Download className="w-4 h-4 mr-2" /> Download PDF
                  </Button>
                  <Button variant="outline" onClick={downloadCSV} data-testid="download-csv">
                    <FileSpreadsheet className="w-4 h-4 mr-2" /> Export CSV
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            <KpiCard label="Invoices" value={summary.totals.invoice_count} color="text-[#023E8A]" />
            <KpiCard label="Total Sales" value={fmt(summary.totals.total_sales)} color="text-[#2B2D42]" />
            <KpiCard label="Gross Profit" value={fmt(summary.totals.gross_profit)} color="text-green-600" />
            <KpiCard label="FTA Tax Due" value={fmt(summary.totals.fta_tax_amount)} color="text-red-600" />
            <KpiCard label="Net Profit" value={fmt(summary.totals.net_profit)} color="text-[#D97706]" />
          </div>

          {/* Per-company tables */}
          {summary.companies.map((c) => (
            <Card key={c.company_id} className="mb-6">
              <CardHeader className="bg-[#023E8A]/5 border-b">
                <CardTitle className="text-base flex items-center justify-between">
                  <span className="text-[#023E8A]">{c.company_name}</span>
                  <span className="text-xs font-normal text-gray-600">
                    {c.tax_id_label || 'TRN'}: {c.trn || '—'} · License: {c.license_number || '—'}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="grid grid-cols-2 md:grid-cols-6 gap-0 border-b bg-gray-50 text-center text-xs">
                  {[
                    ['Invoices', c.totals.invoice_count],
                    ['Total Sales', `${c.currency} ${fmt(c.totals.total_sales)}`],
                    ['Total Cost', `${c.currency} ${fmt(c.totals.total_cost)}`],
                    ['Gross Profit', `${c.currency} ${fmt(c.totals.gross_profit)}`, 'text-green-700'],
                    [`FTA ${c.totals.avg_fta_rate}%`, `${c.currency} ${fmt(c.totals.fta_tax_amount)}`, 'text-red-700'],
                    ['Net Profit', `${c.currency} ${fmt(c.totals.net_profit)}`, 'text-amber-700'],
                  ].map(([lbl, val, cls]) => (
                    <div key={lbl} className="py-3 px-2 border-r last:border-r-0">
                      <div className="text-[10px] font-semibold text-gray-500 uppercase">{lbl}</div>
                      <div className={`text-sm font-bold ${cls || 'text-gray-900'} mt-1`}>{val}</div>
                    </div>
                  ))}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[#023E8A] text-white">
                        <th className="p-2 text-left">Invoice #</th>
                        <th className="p-2 text-left">Date</th>
                        <th className="p-2 text-left">Client</th>
                        <th className="p-2 text-center">Ccy</th>
                        <th className="p-2 text-right">Sale</th>
                        <th className="p-2 text-right">Cost</th>
                        <th className="p-2 text-right">Gross Profit</th>
                        <th className="p-2 text-center">FTA%</th>
                        <th className="p-2 text-right">FTA Tax</th>
                        <th className="p-2 text-right">Net Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.invoices.map((inv, i) => (
                        <tr key={inv.invoice_number} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="p-2 font-mono">{inv.invoice_number}</td>
                          <td className="p-2">{(inv.issue_date || '').slice(0, 10)}</td>
                          <td className="p-2">{inv.client_name || '—'}</td>
                          <td className="p-2 text-center">{inv.currency}</td>
                          <td className="p-2 text-right font-mono">{fmt(inv.total_sale)}</td>
                          <td className="p-2 text-right font-mono text-gray-700">{fmt(inv.total_cost)}</td>
                          <td className="p-2 text-right font-mono text-green-700">{fmt(inv.gross_profit)}</td>
                          <td className="p-2 text-center">{inv.fta_tax_rate?.toFixed(1)}%</td>
                          <td className="p-2 text-right font-mono text-red-700">{fmt(inv.fta_tax_amount)}</td>
                          <td className="p-2 text-right font-mono font-bold text-amber-700">{fmt(inv.net_profit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          ))}

          {summary.companies.length === 0 && (
            <div className="text-center py-12 text-gray-500 bg-white rounded-lg border">
              No invoices found for this period.
            </div>
          )}
        </>
      )}
    </div>
  );
};

const KpiCard = ({ label, value, color }) => (
  <div className="bg-white border rounded-lg p-4 text-center shadow-sm">
    <div className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">{label}</div>
    <div className={`text-xl font-bold ${color} mt-1`}>{value}</div>
  </div>
);

export default FTAFiling;
